import type { QueryKey } from '@tanstack/react-query';
import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
  type DynamicFilterOptionsPage,
  type FilterOptionsParams,
} from '@/components/DataTable';
import {
  BROWSER_AI_TOOLS_FILTERABLE_FIELDS,
  BROWSER_AI_TOOLS_TREND_OPTIONS,
  type BrowserAiToolListItem,
  type BrowserAiNavigationType,
  type BrowserAiToolsFilterOptionField,
} from '@kirin/api-client/contracts/shadow-ai-browser-tools';
import { getShadowAiCategoryLabel } from '@kirin/api-client/contracts/shadow-ai';
import { BROWSER_AI_TOOLS_FILTER_URL_KEYS } from './constants';
import {
  DomainCell,
  NameCell,
  CategoryCell,
  TrendCell,
  UsersCell,
  VisitsCell,
  FirstDetectedCell,
  LastSeenCell,
} from '@/components/shared/shadow-ai/BrowserToolCells';
import { BROWSER_TOOLS_LAST_SEEN_TOOLTIP } from '@/components/shared/shadow-ai';
import {
  DomainFilterOption,
  BrowserAiToolsUserFilterOption,
  CategoryFilterOption,
  TrendFilterOption,
} from '@/components/shared/shadow-ai/BrowserToolFilterComponents';
import { fetchBrowserAiToolsFilterOptions } from './api';
import { queryKeys } from '@/hooks/api/queryKeys';

const EmptyOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const StringOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  { value: FilterOperators.CONTAINS, label: 'Contains' },
  { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
  ...EmptyOperators,
];

const EnumOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  ...EmptyOperators,
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
  ...EmptyOperators,
];

export function createBrowserAiToolsColumns(
  organizationId: string,
  navigationType?: BrowserAiNavigationType,
  // KIR-3495: on the Shadow AI user-scope page the dropdowns must offer only
  // the values present in that member's rows, not the whole organization's.
  scopeUserId?: string
): ColumnConfig<BrowserAiToolListItem>[] {
  const optionsQueryKey = (
    field: BrowserAiToolsFilterOptionField,
    params: FilterOptionsParams
  ): QueryKey => {
    const base = queryKeys.shadowAi.browserAiToolsFilterOptions(
      organizationId,
      field,
      params
    );
    // Keep the org-wide key byte-identical when unscoped so the existing cache
    // entries stay valid.
    return scopeUserId ? [...base, { scopeUserId }] : [...base];
  };

  const fetchOptions = (
    field: BrowserAiToolsFilterOptionField,
    params: FilterOptionsParams
  ): Promise<DynamicFilterOptionsPage> =>
    fetchBrowserAiToolsFilterOptions(
      organizationId,
      field,
      params,
      scopeUserId
    );

  return [
    {
      id: 'domain',
      header: 'Domain',
      accessor: 'domain',
      renderCell: ({ row }) => <DomainCell row={row} />,
      width: 220,
      sortable: true,
      sortField: 'domain',
      sortKey: 'dom',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey:
          BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.DOMAIN
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params => optionsQueryKey('domain', params),
        queryFn: params => fetchOptions('domain', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <DomainFilterOption domain={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      renderCell: ({ row }) => <NameCell row={row} />,
      width: 200,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey:
          BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.NAME
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params => optionsQueryKey('name', params),
        queryFn: params => fetchOptions('name', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: 'category',
      header: 'Category',
      accessor: 'category',
      renderCell: ({ row }) => <CategoryCell row={row} />,
      width: 160,
      sortable: true,
      sortField: 'category',
      sortKey: 'cat',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey:
          BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.CATEGORY
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params => optionsQueryKey('category', params),
        queryFn: params => fetchOptions('category', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        labelFormatter: getShadowAiCategoryLabel,
        render: (option, isSelected) => (
          <CategoryFilterOption
            category={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'trend',
      header: 'Trend',
      accessor: 'trend',
      renderCell: ({ row }) => <TrendCell row={row} />,
      width: 130,
      sortable: true,
      sortField: 'trend',
      sortKey: 'tr',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey:
          BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.TREND
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [...BROWSER_AI_TOOLS_TREND_OPTIONS],
        render: (option, isSelected) => (
          <TrendFilterOption trend={option.value} isSelected={isSelected} />
        ),
      },
    },
    // KIR-3495: scoped to one member the count is always 1 — drop the column.
    ...(scopeUserId
      ? []
      : [
          {
            // The id IS the filter field sent to the API (DataTable passes
            // `fieldId={column.id}`), so it must be the contract's filterable field
            // name — `users`, not the `userCount` the cell happens to render. With
            // `userCount` the backend silently dropped the filter and returned every
            // row (KIR-3495).
            id: BROWSER_AI_TOOLS_FILTERABLE_FIELDS.USERS,
            header: 'Users',
            accessor: 'userCount',
            renderCell: ({ row }) => <UsersCell row={row} />,
            width: 100,
            sortable: true,
            sortField: 'users',
            sortKey: 'uc',
            filterable: true,
            dataType: ColumnDataTypes.ENUM,
            filterOperators: EnumOperators,
            statePersistence: {
              urlKey:
                BROWSER_AI_TOOLS_FILTER_URL_KEYS[
                  BROWSER_AI_TOOLS_FILTERABLE_FIELDS.USERS
                ],
            },
            filterOptions: {
              type: FilterOptionTypes.DYNAMIC,
              getQueryKey: params => optionsQueryKey('users', params),
              queryFn: params => fetchOptions('users', params),
              pagination: { mode: 'offset', limit: 10 },
              queryConfig: { staleTime: 2 * 60 * 1000 },
              render: (option, isSelected) => (
                <BrowserAiToolsUserFilterOption
                  userId={option.value}
                  userName={(option.metadata?.name as string | null) ?? null}
                  userEmail={(option.metadata?.email as string | null) ?? null}
                  isSelected={isSelected}
                />
              ),
            },
          } satisfies ColumnConfig<BrowserAiToolListItem>,
        ]),
    {
      id: 'visits',
      header: 'Visits',
      accessor: 'visitCount',
      renderCell: ({ row }) => (
        <VisitsCell row={row} navigationType={navigationType} />
      ),
      width: 130,
      sortable: false,
    },
    {
      id: 'firstDetected',
      header: 'First Detected',
      accessor: 'firstDetected',
      renderCell: ({ row }) => <FirstDetectedCell row={row} />,
      width: 150,
      sortable: true,
      sortField: 'firstDetected',
      sortKey: 'fd',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: 'lastSeen',
      header: 'Last Seen',
      description: BROWSER_TOOLS_LAST_SEEN_TOOLTIP,
      accessor: 'lastSeen',
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      width: 150,
      sortable: true,
      sortField: 'lastSeen',
      sortKey: 'ls',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          BROWSER_AI_TOOLS_FILTER_URL_KEYS[
            BROWSER_AI_TOOLS_FILTERABLE_FIELDS.LAST_SEEN
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}
